import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, LogIn } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/form-field';
import { apiLogin } from './api';
import { loginSchema, type LoginInput } from './schemas';
import { useAuthStore } from './store';
import { extractErrorMessage } from '@/lib/api-client';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (values: LoginInput) => {
    setSubmitError(null);
    try {
      const result = await apiLogin(values);
      setSession(result);
      const redirectTo =
        (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/app';
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setSubmitError(extractErrorMessage(err));
    }
  };

  return (
    <div className="space-y-7">
      <div className="space-y-2">
        <h2 className="text-3xl font-semibold tracking-tight">Entrar</h2>
        <p className="text-sm text-muted-foreground">
          Acesse o painel para gerenciar sua frota.
        </p>
      </div>

      {submitError && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <FormField
          control={form.control}
          name="email"
          label="E-mail"
          type="email"
          placeholder="voce@empresa.com.br"
          autoComplete="email"
          autoFocus
        />
        <FormField
          control={form.control}
          name="password"
          label="Senha"
          type="password"
          placeholder="••••••••••"
          autoComplete="current-password"
        />

        <div className="flex items-center justify-end">
          <Link
            to="/forgot-password"
            className="text-xs font-medium text-primary hover:underline"
          >
            Esqueci minha senha
          </Link>
        </div>

        <Button
          type="submit"
          size="lg"
          className="w-full"
          loading={form.formState.isSubmitting}
        >
          <LogIn className="size-4" />
          Entrar
        </Button>
      </form>

      <p className="text-center text-sm text-muted-foreground">
        Ainda não tem conta?{' '}
        <Link to="/register" className="font-medium text-primary hover:underline">
          Cadastre sua empresa
        </Link>
      </p>
    </div>
  );
}
